import { createTheme } from '@mui/material/styles';

// Color palette based on demo_1 theme
const colors = {
  primary: {
    main: '#4CAEB8',       // Teal/Cyan - demo_1 primary (p300)
    hover: '#267D88',      // Darker teal (p400)
    light: '#82D8E1',      // Lighter teal (p200)
    dark: '#267D88',       // Dark teal (p400)
  },
  secondary: {
    main: '#FBB24C',       // Orange/Gold - demo_1 secondary (s300)
    hover: '#D6923A',      // Darker orange (s400)
    light: '#FED4A0',      // Lighter orange (s200)
    dark: '#D6923A',       // Dark orange (s400)
  },
  success: {
    main: '#20C997',       // Green - strikingDash success
    light: '#5DD4B4',
    dark: '#0CAB7C',
  },
  warning: {
    main: '#FA8B0C',       // Orange - strikingDash warning
    light: '#FCAD48',
    dark: '#D47407',
  },
  error: {
    main: '#FF4D4F',       // Red - strikingDash danger
    light: '#FF7875',
    dark: '#E30D0F',
  },
  info: {
    main: '#2C99FF',       // Blue - strikingDash info
    light: '#5DB1FF',
    dark: '#0D79DF',
  },
  dark: {
    main: '#272B41',       // Dark color - strikingDash
    light: '#5A5F7D',      // Gray color
    dark: '#000000',
  },
  gray: {
    main: '#5A5F7D',       // Gray color - strikingDash
    light: '#9299B8',      // Light gray
    dark: '#272B41',       // Dark gray
  },
  background: {
    default: '#F4F5F7',    // Background gray (keeping previous color)
    paper: '#FFFFFF',
  },
  text: {
    primary: '#272B41',    // Dark color
    secondary: '#5A5F7D',  // Gray color
    disabled: '#9299B8',   // Light gray
  },
  border: {
    light: '#F1F2F6',
    normal: '#E3E6EF',
    deep: '#C6D0DC',
  },
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
    info: colors.info,
    background: colors.background,
    text: colors.text,
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0 5px 20px rgba(146,153,184,0.03)',
    '0 5px 20px rgba(146,153,184,0.05)',
    '0 5px 20px rgba(146,153,184,0.08)',
    '0 5px 20px rgba(146,153,184,0.12)',
    '0 5px 25px rgba(146,153,184,0.15)',
    '0 5px 30px rgba(146,153,184,0.18)',
    '0 10px 35px rgba(146,153,184,0.22)',
    '0 10px 40px rgba(146,153,184,0.25)',
    '0 15px 45px rgba(146,153,184,0.28)',
    '0 15px 50px rgba(146,153,184,0.32)',
    '0 20px 55px rgba(146,153,184,0.35)',
    '0 20px 60px rgba(146,153,184,0.38)',
    '0 25px 65px rgba(146,153,184,0.42)',
    '0 25px 70px rgba(146,153,184,0.45)',
    '0 30px 75px rgba(146,153,184,0.48)',
    '0 30px 80px rgba(146,153,184,0.52)',
    '0 35px 85px rgba(146,153,184,0.55)',
    '0 35px 90px rgba(146,153,184,0.58)',
    '0 40px 95px rgba(146,153,184,0.62)',
    '0 40px 100px rgba(146,153,184,0.65)',
    '0 45px 105px rgba(146,153,184,0.68)',
    '0 45px 110px rgba(146,153,184,0.72)',
    '0 50px 115px rgba(146,153,184,0.75)',
    '0 50px 120px rgba(146,153,184,0.78)',
  ],
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          color: '#FFFFFF',
          backgroundColor: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.hover,
            boxShadow: '0 4px 12px rgba(76, 174, 184, 0.3)',
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: colors.secondary.main,
            '&:hover': {
              backgroundColor: colors.secondary.hover,
              boxShadow: '0 4px 12px rgba(251, 178, 76, 0.3)',
            },
          },
          '&.MuiButton-containedSuccess': {
            backgroundColor: colors.success.main,
            '&:hover': {
              backgroundColor: colors.success.dark,
              boxShadow: '0 4px 12px rgba(32, 201, 151, 0.3)',
            },
          },
          '&.MuiButton-containedError': {
            backgroundColor: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.dark,
              boxShadow: '0 4px 12px rgba(255, 77, 79, 0.3)',
            },
          },
          '&.MuiButton-containedWarning': {
            backgroundColor: colors.warning.main,
            '&:hover': {
              backgroundColor: colors.warning.dark,
              boxShadow: '0 4px 12px rgba(250, 139, 12, 0.3)',
            },
          },
          '&.MuiButton-containedInfo': {
            backgroundColor: colors.info.main,
            '&:hover': {
              backgroundColor: colors.info.dark,
              boxShadow: '0 4px 12px rgba(44, 153, 255, 0.3)',
            },
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor: colors.primary.main,
          color: colors.primary.main,
          '&:hover': {
            borderWidth: 1,
            borderColor: colors.primary.hover,
            backgroundColor: `${colors.primary.main}10`,
          },
          '&.MuiButton-outlinedSecondary': {
            borderColor: colors.secondary.main,
            color: colors.secondary.main,
            '&:hover': {
              borderColor: colors.secondary.hover,
              backgroundColor: `${colors.secondary.main}10`,
            },
          },
        },
        text: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: `${colors.primary.main}10`,
          },
          '&.MuiButton-textSecondary': {
            color: colors.text.secondary,
            '&:hover': {
              backgroundColor: colors.gray.light + '10',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary.main,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}15`,
            '&:hover': {
              backgroundColor: `${colors.primary.main}20`,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: 'none',
          border: 'none',
          backgroundColor: '#FFFFFF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          backgroundColor: '#FFFFFF',
          color: colors.text.primary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        colorSuccess: {
          backgroundColor: `${colors.success.main}15`,
          color: colors.success.dark,
        },
        colorError: {
          backgroundColor: `${colors.error.main}15`,
          color: colors.error.dark,
        },
        colorWarning: {
          backgroundColor: `${colors.warning.main}15`,
          color: colors.warning.dark,
        },
        colorInfo: {
          backgroundColor: `${colors.info.main}15`,
          color: colors.info.dark,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        standardError: {
          backgroundColor: `${colors.error.main}10`,
          color: colors.error.dark,
        },
        standardWarning: {
          backgroundColor: `${colors.warning.main}10`,
          color: colors.warning.dark,
        },
        standardInfo: {
          backgroundColor: `${colors.info.main}10`,
          color: colors.info.dark,
        },
        standardSuccess: {
          backgroundColor: `${colors.success.main}10`,
          color: colors.success.dark,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: {
          backgroundColor: colors.primary.main,
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 48,
          fontWeight: 500,
          fontSize: '0.875rem',
          color: colors.text.secondary,
          '&:hover': {
            color: colors.primary.main,
            opacity: 1,
          },
          '&.Mui-selected': {
            color: colors.primary.main,
            fontWeight: 600,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover': {
            backgroundColor: `${colors.primary.main}10`,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.dark.main,
          fontSize: '0.75rem',
          borderRadius: 4,
        },
      },
    },
    MuiStack: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 6,
          backgroundColor: `${colors.primary.main}15`,
        },
        bar: {
          borderRadius: 4,
          backgroundColor: colors.primary.main,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.normal,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            color: colors.primary.hover,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          boxShadow: '0 20px 60px rgba(146,153,184,0.3)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px',
          borderBottom: `1px solid ${colors.border.normal}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${colors.border.normal}`,
          gap: 12,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          minWidth: 650,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#F4F5F7',
          fontWeight: 600,
          fontSize: '12px',
          color: colors.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          padding: '16px',
          borderBottom: `1px solid ${colors.border.normal}`,
          whiteSpace: 'nowrap',
        },
        body: {
          fontSize: '13px',
          color: colors.text.primary,
          padding: '14px 16px',
          borderBottom: `1px solid ${colors.border.normal}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(76, 174, 184, 0.04)',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
          '&.Mui-selected': {
            backgroundColor: `${colors.primary.main}15`,
            '&:hover': {
              backgroundColor: `${colors.primary.main}20`,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: colors.primary.main,
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 20,
          height: 20,
        },
        track: {
          borderRadius: 12,
          backgroundColor: colors.gray.light,
          opacity: 1,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.gray.light,
          '&.Mui-checked': {
            color: colors.primary.main,
          },
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.gray.light,
          '&.Mui-checked': {
            color: colors.primary.main,
          },
          '&:hover': {
            backgroundColor: `${colors.primary.main}08`,
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: colors.error.main,
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.main,
          color: '#fff',
          fontWeight: 600,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: colors.text.secondary,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: 'none',
          border: `1px solid ${colors.border.normal}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          '&:hover': {
            backgroundColor: `${colors.primary.main}05`,
          },
        },
      },
    },
  },
});

export default theme;
